import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  IconButton,
  LightModeSemanticColors,
  MenuRoot,
  MenuContent as RawMenuContent,
  MenuItem as RawMenuItem,
  MenuTrigger as RawMenuTrigger,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import {
  FaCircleExclamationSolid,
  FaEllipsisRegular,
  FaTriangleExclamationSolid,
} from '@enechain/ecloud-icons'

import { BidMemoCell } from '~/components/bidding/BidMemoCell'
import { SendErrorReasonTooltip } from '~/components/bidding/SendErrorReasonTooltip'
import { Badge } from '~/components/common/Badge'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { type EnumBadgeSpec } from '~/components/common/EnumStatusBadge'
import {
  TABLE_HEADER_BG,
  TABLE_HEADER_CELL_STYLE,
} from '~/components/common/MasterDataTable'
import {
  STICKY_ACTION_HOVER_OVERLAY,
  stickyActionCellStyle,
  stickyActionHeaderStyle,
  useStickyRightShadow,
} from '~/components/common/MasterDataTable/stickyActionColumn'
import { TableMessageRow } from '~/components/common/TableMessageRow'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { iconSize } from '~/constants/iconSize'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { convertAreaLabel } from '~/models/area'
import {
  KOMA_DIVIDER_ROW_CSS,
  komaListDividerFlags,
} from '~/models/komaDivider'
import { komaNumberTimeRange } from '~/models/komaToTime'
import { WithdrawStateBadge } from '~/pages/SpotBidding/components/WithdrawStateBadge'
import {
  listedWithdrawState,
  resolveSpotBidWithdrawState,
  withdrawStateHintKey,
} from '~/pages/SpotBidding/models/bidWithdrawState'
import { formatCreatedLabel } from '~/pages/SpotBidding/models/createdLabel'
import { canReturnToDraft } from '~/pages/SpotBidding/models/returnToDraftAction'
import { spotBidRemainingVolumeMw } from '~/pages/SpotBidding/models/spotBidMatchOutcome'
import {
  type SpotBidRow,
  isMarketOrderRow,
} from '~/pages/SpotBidding/models/spotBidRow'
import {
  SPOT_BID_STATUS_CATEGORY_LABEL_KEY,
  type SpotBidStatusCategory,
  isExpiryConfirmed,
} from '~/pages/SpotBidding/models/spotBidStatusCategory'
import { resolveSpotBidWithdrawAction } from '~/pages/SpotBidding/models/spotBidWithdrawAction'
import { hasSpotSendErrorReason } from '~/pages/SpotBidding/models/spotSendErrorReason'

import { NeedsReviewChip } from './NeedsReviewChip'

/**
 * chakra v3 の `MenuTriggerProps` / `MenuContentProps` / `MenuItemProps` は本プロジェクトの
 * 型解決 (ecloud 0.14 / chakra 3.29 の d.ts 解決問題) で `HTMLChakraProps` が collapse し
 * `children` / `value` / `onSelect` / style props が欠落する。 v3 本来の prop シグネチャ
 * (`HTMLChakraProps<element>` + ark-ui 固有 prop) へ cast して復元する。 ランタイムは ecloud のまま。
 */
const MenuTrigger = RawMenuTrigger as React.FC<
  HTMLChakraProps<'button'> & { asChild?: boolean }
>
const MenuContent = RawMenuContent as React.FC<HTMLChakraProps<'div'>>
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & {
    value: string
    disabled?: boolean
    onSelect?: () => void
  }
>

type Props = {
  memberBidDisabledReason?: string
  rows: SpotBidRow[]
  tranchesMaster: Array<{ id: string; name: string }>
  /** GC 後 (= post) なら 約定価格 / 量 / status 列を表示。 */
  isPost: boolean
  /**
   * 行のステータス列の 12 区分 (AC-BSP-047)。
   *
   * 区分は呼び出し側 (タブ) が [[deriveSpotBidStatusCategory]] と締切判定
   * ([[isSpotRowPastDeadline]]) から行ごとに 1 回だけ導き、 ステータスフィルタ
   * (AC-BSP-019a) と同じ関数をここへ渡す。 表の中で導出し直すと、 フィルタで選んだ
   * 区分と表示される区分が別々の締切値で決まり得るため (#8578)。 渡し忘れで締切後の
   * 札が黙って誤表示されないよう必須にしている。
   */
  statusCategoryOf: (row: SpotBidRow) => SpotBidStatusCategory
  /** 売買逆転 (AC-BSP-009) の対象 bid id set。 warning 系でハイライトする。 */
  inversionBidIds?: Set<string>
  /** 入札制限違反の対象 bid id set。 negative 系でハイライトする。 */
  limitViolationBidIds?: Set<string>
  onEditBid?: (row: SpotBidRow) => void
  onDeleteBid?: (row: SpotBidRow) => void
  /**
   * 入札中の札の「取下」 (AC-BSP-057)。 未指定なら取下を出さない
   * (= 画面が read-only / 履歴タブ)。 出すかどうかの行単位判定は
   * [[resolveSpotBidWithdrawAction]] に閉じる。
   */
  onWithdrawBid?: (row: SpotBidRow) => void
  /**
   * 送信中の申請セットの「下書きに戻す」 (AC-BSP-117 / AC-BSP-129)。 押した行の申請セットの
   * 送信中の札が戻る (入札中の札は戻らない)。 未指定なら出さない。 出すかどうかの行単位判定は [[canReturnToDraft]]。
   */
  onReturnToDraft?: (row: SpotBidRow) => void
  /**
   * 取消済みの札からの「再入札」 (AC-BSP-081)。 未指定なら出さない。
   * 取消と再入札は内部で自動連結しない — この callback は新しい札の作成画面を
   * 開くだけで、 取消札には触れない。
   */
  onRebidBid?: (row: SpotBidRow) => void
  /**
   * 入札締切前か。 取下 / 再入札 はいずれも締切前限定 (AC-BSP-057 /
   * AC-BSP-013)。 既定 false = 出さない側に倒す。
   */
  isBeforeDeadline?: boolean
  /**
   * 確定済ワークフロー経由で画面が read-only か (AC-BSP-058)。 既定 false。
   */
  isWorkflowReadonly?: boolean
  /**
   * 行クリックで札を開く (design `spot.html`「入札一覧テーブル: 行クリック → 編集
   * モーダル起動」/ #5356 Des 裁定 2026-08-18「全行開く・下書き以外は表示専用」)。
   * 編集できる札かどうかの判断は呼び出し側が `row.approvalState` で行う。
   */
  onOpenBid?: (row: SpotBidRow) => void
  /**
   * 「複製」(AC-BSP-011「ユーザーは入札内容を翌営業日向けに再利用できる」)。
   * 元の失効札は変更せず、 内容 (エリア/売買/コマ/価格/数量/メモ) を引き継いだ
   * 別の新規札として作成する導線を呼び出し側へ委ねる (= 呼び出し側が個別作成
   * モーダルを新規作成モードで開く)。 表示条件はこのコンポーネント内で行単位に
   * 判定する (= `statusCategoryOf` の区分が 'expired' で、 かつ失効が確定している
   * 行のみ。 [[isExpiryConfirmed]])。
   */
  onDuplicateBid?: (row: SpotBidRow) => void
  /**
   * 行が 0 件の時に出す中身。 省略時は「該当する札がありません」 (絞り込み後の 0 件)。
   * 受渡日に札が 1 件も無い時の空状態 (AC-BSP-020) は呼び出し側が渡す。
   */
  emptyContent?: React.ReactNode
}

/**
 * 列幅のフロア (= 下限)。 design `spot.html` の `#spot-bid-table th/td:nth-child(n)`
 * (2026-08-14 / #5362) の実測値をそのまま写す。
 *
 * この表は auto layout (`table-layout` 既定) なので、 列幅が「今表示されている行の内容」で
 * 決まる。 売/買 フィルタ等で行が減ると列幅が動いて一覧の見た目が変わり、 0 件になると
 * 全列がヘッダ幅まで collapse する (#5362)。 各列に下限を与えて内容非依存にする。
 *
 * - `width` ではなく `minWidth` を使う。 器 < 表の min-content になる表では Chrome が
 *   min-content 分配モードに入り、 セルの `width` は無視される (design 実測: 操作列の
 *   `width:80px` が 70px / 16px になっていた)。 `minWidth` は下限なので、 内容がこれを
 *   超える列は伸びる = 文字の重なり・切れは起きない。
 * - th と td の両方に置く (design と同じ)。 header は絞り込み・0 件に関わらず必ず全列を
 *   描くので 0 件時の崩壊は header 側で止まり、 td 側は行がある時の揺れを止める。
 * - design は列を CSS の nth-child で指定しているが、 実アプリは列を JSX の条件分岐
 *   (`isPost` / `!isPost`) で出し分けるため index が phase で変わる。 index ではなく
 *   列名キーで持ち、 th / td の双方から同じ値を参照する。
 */
const COL_MIN_WIDTH = {
  /** ステータス (badge「部分約定」+ 警告アイコン)。 */
  status: '124px',
  /** 時間帯 09:00-09:30。 */
  timeBand: '116px',
  /**
   * 受渡契約コード (AC-BSP-001。 2026-09-08 spec 決定で発注主体列を置換)。
   *
   * design `spot.html` はまだ本列を持たない (旧発注主体列のまま、 drift)。
   * 幅は置換前の発注主体列の実測値 (180px) をそのまま踏襲する。
   */
  deliveryContractCode: '180px',
  /** エリア (ヘッダ律速)。 */
  area: '92px',
  /** 売/買 (ヘッダ律速)。 */
  side: '88px',
  /** 入札価格 (ヘッダ 2 行目「(円/kWh)」律速)。 */
  bidPrice: '116px',
  /** 入札量 (数値列の基準幅)。 */
  bidVolume: '96px',
  /** 約定価格。 */
  execPrice: '116px',
  /** 約定量。 */
  execVolume: '96px',
  /** 残数量 (AC-BSP-002)。 design に列が無いため、 同じ数量列の約定量と同じ幅にする。 */
  remainingVolume: '96px',
  /**
   * 入札番号 (AC-BSP-001。 JEPX 発番、 15 桁固定)。 design `spot.html`
   * `#spot-bid-table th:nth-child(8)` の実測値をそのまま踏襲する。
   */
  bidNo: '160px',
  /** メモ (JEPX提出用) = design「JEPX自由記述欄」。 セルの maxWidth 180px と同値。 */
  memoJepx: '180px',
  /** メモ (自由記述) = design「メモ」。 同上。 */
  memoFree: '180px',
  /**
   * ワークフロー ID (AC-BSP-001)。 design `spot.html`
   * `#spot-bid-table th:nth-child(12)` の実測値 (「ワークフロー ID
   * M1-20260901-0B1F」) をそのまま踏襲する。
   */
  workflowId: '164px',
  /**
   * 作成。 design `spot.html` `#spot-bid-table th:nth-child(15)` の実測値
   * (「自動生成 (Bot) 2026/08/14 06:14」= 218.2px。 #10124 で年を足した後)。
   */
  createdAt: '219px',
  /** 操作 (「取下」ボタン)。 */
  actions: '80px',
} as const

/** ハイライトの種別。 行の `data-row-highlight` に出す (テストの観測点)。 */
type RowHighlightKind = 'inversion' | 'limit-violation'

type RowHighlight = {
  /** 行の地色。 */
  backgroundColor: string
  /**
   * 右端固定の操作列の地色 (`backgroundColor` と同じ色の実値)。 固定セルは親 `tr` の
   * 背景を継承できず、 透明だと下を流れる他列が透けるため、 セル側にも同じ色を持たせる。
   */
  stickyFill: string
}

/**
 * 行ハイライトの描き分け (#5537「売買逆転を目立たせたい」/ Des 裁定 2026-08-17)。
 *
 * - **赤には寄せない。** negative (赤) は「検出時に送信を自動抑止する」入札制限違反に
 *   割り当て済みで、 売買逆転は spec `AC-BSP-009` が「警告アラート + 対象行ハイライト、
 *   ブロックしない」 と規定する (= 意図的な逆転があり得る)。 逆転を赤にすると「止まる
 *   違反」と「止まらない警告」が同じ見た目になる。
 * - **地に `Surface.warningMid` は使わない。** DS の `Badge` (warning) の地と同一 token
 *   なので、 逆転行のステータス badge の器だけが消える (面比 1.00)。 地は
 *   `Surface.warningLow` に置く。
 *
 * [DS 差異 / design 差異] design `bidding/spot.html` は地の薄さを補うために
 * **行の左端に 3px マーカー** (`.inversion-row > td:first-child` の background-image) を
 * 持つが、 **実装では出さない** (2026-08-18 デザイナー指摘「行の左に太い border UI
 * やめたい」)。 マーカーは「地の色だけでは弱い」への手当てだったところ、 #5623 対応で
 * ステータス列に警告アイコン (△! / ◯!) が入り、 **種別の判別と視認性はアイコンが持つ**
 * ようになったため役割が重複する。 色覚特性・支援技術への担保もアイコンの
 * **グリフの形** + `role="img"` + `aria-label` 側にあるので、 マーカー撤去で
 * WCAG 1.4.1 の根拠は失われない。 design 側の追随は別途起票する。
 */
const ROW_HIGHLIGHT: Record<RowHighlightKind, RowHighlight> = {
  inversion: {
    backgroundColor: 'Surface.warningLow',
    stickyFill: LightModeSemanticColors.Surface.warningLow.value,
  },
  'limit-violation': {
    backgroundColor: 'Surface.negativeLow',
    stickyFill: LightModeSemanticColors.Surface.negativeLow.value,
  },
}

/**
 * 行に出すハイライト種別を決める。 逆転と制限違反を**両方**持つ行は、 制限違反
 * (= 送信が止まる方) の見た目に寄せる (#5537 Des 裁定)。 テーマによって優先順位が
 * 入れ替わらないよう、 判定は 1 箇所に閉じる。
 */
const resolveRowHighlight = (
  bidId: string,
  inversionBidIds: Set<string> | undefined,
  limitViolationBidIds: Set<string> | undefined,
): RowHighlightKind | undefined => {
  if (limitViolationBidIds?.has(bidId) === true) {
    return 'limit-violation'
  }
  if (inversionBidIds?.has(bidId) === true) {
    return 'inversion'
  }
  return undefined
}

/**
 * ステータス列の警告アイコン枠 (#5623 Des 裁定「案 4 = アイコンを足す」)。
 *
 * 売買逆転と入札制限違反は行の地色でも描き分けているが、 **色だけ**では支援技術
 * (スクリーンリーダー) と色覚特性のあるユーザーに 2 種の違いが伝わらない
 * (WCAG 1.4.1「色だけを情報伝達の唯一の視覚的手段にしない」)。 色ではなく
 * **グリフの形の違い** (△! = 逆転 / ◯! = 違反) で担保する。
 *
 * - 逆転 = `Text.warningHigh` / 違反 = `Text.negativeHigh`
 * - hover の説明は **DS `Tooltip`**。 native `title` は表示まで約 1 秒かかり見た目も
 *   OS 依存なので、 表示の主役は Tooltip にする (2026-08-18 デザイナー指摘)。
 *   `title` は `RowActionButtons` と同じく **Tooltip 非対応環境 (タッチ・支援技術の
 *   一部) 向けの保険**として併記する
 * - `role="img"` + `aria-label`。 素の span は generic role なので、 tooltip だけでは
 *   支援技術の読み上げ名にならない
 *
 * 行の地色は「両方該当なら制限違反に寄せる」(#5537 裁定) が、 アイコンは**両方出す**。
 * 地色は 1 行 1 色しか持てないが、 アイコンは 2 種を同時に示せるため。
 *
 * [design 差異] design `bidding/spot.html` の `.bid-status-alert-icon` は
 * **14px × 2 = 28px の固定枠**で、 該当しない側を空 placeholder が占有する
 * (= 行を越えてアイコンの縦列と badge の左端が揃う)。 実装は**空 placeholder を
 * 置かない** (2026-08-18 デザイナー指摘「△アイコンとバッジの間隔が遠い」)。
 * 間隔が空いて見える原因は 4px の隙間ではなく、 もう一方のアイコン用に確保していた
 * この 14px の空枠だったため。 撤去により該当が 1 種類だけの行でアイコンが badge の
 * 直前に来る代わりに、 **badge の左端は行によって最大 20px ずれる**
 * (アイコン 0 / 1 / 2 個)。 縦列の揃いより「アイコンと badge が離れて見えない」ことを
 * 優先するデザイナー判断。 design 側の追随は別途起票する。
 */
const BidStatusAlertIcons = ({
  isInversion,
  isLimitViolation,
}: {
  isInversion: boolean
  isLimitViolation: boolean
}): React.ReactElement | null => {
  const { t } = useTranslation()
  // 該当なしの行は要素ごと出さない (親 flex の gap を消費させないため)
  if (!isInversion && !isLimitViolation) return null
  return (
    <Box alignItems="center" flexShrink={0} gap={0} display="inline-flex">
      {isInversion && (
        <Tooltip content={t('page:biddingSpot.bidTable.alertIcon.inversion')}>
          <Box
            alignItems="center"
            justifyContent="center"
            display="inline-flex"
            color="Text.warningHigh"
            aria-label={t('page:biddingSpot.bidTable.alertIcon.inversion')}
            role="img"
            title={t('page:biddingSpot.bidTable.alertIcon.inversion')}
          >
            <FaTriangleExclamationSolid boxSize={iconSize.gridInline} />
          </Box>
        </Tooltip>
      )}
      {isLimitViolation && (
        <Tooltip
          content={t('page:biddingSpot.bidTable.alertIcon.limitViolation')}
        >
          <Box
            alignItems="center"
            justifyContent="center"
            display="inline-flex"
            color="Text.negativeHigh"
            aria-label={t('page:biddingSpot.bidTable.alertIcon.limitViolation')}
            role="img"
            title={t('page:biddingSpot.bidTable.alertIcon.limitViolation')}
          >
            <FaCircleExclamationSolid boxSize={iconSize.gridInline} />
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}

/**
 * JEPX が受け付けたかを確かめている札の補足 (AC-BSP-117)。 ステータスは変えず、 バッジの
 * 横に添える。 ecloud の `Text` は `fontSize` を無視するので `Box` の span で xs にする。
 */
const ReceiptCheckingNote = (): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Tooltip content={t('page:biddingSpot.bidStatusNote.receiptCheckingHint')}>
      <Box
        as="span"
        color="Text.midEmphasis"
        fontSize="xs"
        whiteSpace="nowrap"
        cursor="help"
        tabIndex={0}
      >
        {t('page:biddingSpot.bidStatusNote.receiptChecking')}
      </Box>
    </Tooltip>
  )
}

/**
 * ステータス列の 12 区分 (AC-BSP-047) → 状態バッジ表示仕様。
 *
 * 色は design `bidding/spot.html` の `statuses` テーブル (#7548 で 12 区分に
 * 拡張済み) の `cls` をそのまま DS semantic status へ写す:
 *   badge-ok → positive / badge-warn → warning / badge-neutral → neutral /
 *   badge-info → info / badge-error → negative
 * ラベルの i18n key は [[SPOT_BID_STATUS_CATEGORY_LABEL_KEY]] を引く (入札一覧の CSV の
 * ステータス列 [[buildNormalBidCsv]] と同じ表を読み、 区分名が画面と CSV で食い違わない)。
 *
 * [[SpotBidStatusCategory]] の網羅 Record なので、 区分が増えるとここが型エラーに
 * なり追従漏れを検出できる。
 */
export const SPOT_BID_STATUS_CATEGORY_BADGE: Record<
  SpotBidStatusCategory,
  EnumBadgeSpec
> = {
  reserved: {
    status: 'info',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.reserved,
  },
  draft: {
    status: 'neutral',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.draft,
  },
  pendingApproval: {
    status: 'warning',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.pendingApproval,
  },
  rejected: {
    status: 'negative',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.rejected,
  },
  sending: {
    status: 'info',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.sending,
  },
  active: {
    status: 'info',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.active,
  },
  awaitingMatch: {
    status: 'warning',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.awaitingMatch,
  },
  allMatched: {
    status: 'positive',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.allMatched,
  },
  partialMatched: {
    status: 'warning',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.partialMatched,
  },
  unmatched: {
    status: 'neutral',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.unmatched,
  },
  cancelled: {
    status: 'neutral',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.cancelled,
  },
  expired: {
    status: 'negative',
    labelKey: SPOT_BID_STATUS_CATEGORY_LABEL_KEY.expired,
  },
}

/**
 * 札のステータス列。
 *
 * 呼び出し側が導いた 12 区分 (AC-BSP-047) を「map 参照 → t() → Badge」 で描く
 * (= `EnumStatusBadge` と同じ形。 区分が proto enum ではなく string union なので
 * 型が合わず共通化できない)。
 */
const SpotBidStatusBadge = ({
  category,
}: {
  category: SpotBidStatusCategory
}): React.ReactElement => {
  const { t } = useTranslation()
  const spec = SPOT_BID_STATUS_CATEGORY_BADGE[category]
  return <Badge status={spec.status}>{t(spec.labelKey)}</Badge>
}

/**
 * 札一覧テーブル。 design HTML `#spot-bid-table` の列構成を再現。
 *
 * - pre-only 列 (= 操作 dropdown) は isPost=false の時のみ表示
 * - post-only 列 (= 約定価格 / 約定量(MW) / 残数量(MW)) は isPost=true の時のみ表示。
 *   収支列は持たない (AC-BSP-002 / AC-BSP-090)。
 * - 送信軸の列は持たない (AC-BSP-001。 design にも無い)。 送れない理由は
 *   ステータス列のバッジの tooltip で見せる ([[hasSpotSendErrorReason]]。 #12494)
 * - 編集・削除操作 (⋯ メニュー) は行単位で `row.approvalState === DRAFT` の
 *   ときのみ表示する (AC-066 / AC-082b: 追加入札 (AC-079) で同一受渡日に複数
 *   request の札が混在する場合、 DRAFT request の札のみ編集・削除できる。
 *   `BlockBidTable` の `canManageGroup` と同型のパターン)。 `onEditBid` /
 *   `onDeleteBid` は常に渡してよく、 表示判定はこのコンポーネント内で行単位に
 *   行う (= 呼び出し側のページ全体 isEditable フラグには依存しない)。
 *
 */
export const SpotBidTable = ({
  rows,
  // tranchesMaster, — トランシェ列は P3 で対応予定のため、それまで非表示 (下記 column / cell 参照)
  isPost,
  statusCategoryOf,
  inversionBidIds,
  limitViolationBidIds,
  onEditBid,
  onDeleteBid,
  onWithdrawBid,
  onReturnToDraft,
  onRebidBid,
  isBeforeDeadline = false,
  isWorkflowReadonly = false,
  onOpenBid,
  onDuplicateBid,
  memberBidDisabledReason,
  emptyContent,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // 作成列の作成者 (= eauth member ID) を表示名へ解決する (生 ID は画面に出さない)。
  const resolveMemberDisplayName = useMemberDisplayName()
  // 操作列 (⋯) を右端に固定する (列が多く横スクロールが常態。 共通 hook / UI 辞書 table.md §1)。
  const { wrapRef, stickyShadow } = useStickyRightShadow([rows])
  // トランシェ列は P3 で対応予定のため、それまで非表示 (下記 column / cell 参照)
  // const tranchesById = new Map(tranchesMaster.map((tr) => [tr.id, tr.name]))

  /**
   * 操作列 (⋯ メニュー) を出すか。 4 つのハンドラが 1 つも無い呼び出しでは、
   * どの行も ⋯ を出せないので**列ごと出さない**。 列だけ残すと、 空の見出しと
   * 80px の余白が並ぶ (PR #5873 codex-review 指摘)。
   */
  const isExpiryConfirmedRow = (row: SpotBidRow): boolean =>
    isExpiryConfirmed(row, statusCategoryOf(row))
  const canDuplicate = (row: SpotBidRow): boolean =>
    onDuplicateBid !== undefined && isExpiryConfirmedRow(row)
  /**
   * 「複製」 (AC-BSP-011) は失効した行にしか出ないため、 ハンドラの有無ではなく
   * 「複製できる行が 1 件でもあるか」 で列の要否を決める。 入札履歴タブは複製の
   * ハンドラを常に受け取るので、 ハンドラの有無で決めると失効札が 1 件も無い時に
   * 空の操作列が戻ってしまう (行と列の判定を [[canDuplicate]] 1 つに揃える)。
   */
  const hasDuplicatableRow = rows.some(canDuplicate)
  const hasRowActions =
    onEditBid !== undefined ||
    onDeleteBid !== undefined ||
    onWithdrawBid !== undefined ||
    onRebidBid !== undefined ||
    onOpenBid !== undefined ||
    hasDuplicatableRow

  /**
   * 「該当する札がありません」 行の colSpan。 列を 2 つの条件 (isPost / hasRowActions) で
   * 出し分けるため定数では合わなくなる (実際 `isPost ? 16 : 12` は操作列が無い履歴タブで
   * 1 列多かった)。 header の分岐と同じ順に数え上げる。
   */
  const columnCount =
    // ステータス / 時間帯 / 受渡契約コード / エリア / 売買 / 入札価格 / 入札量
    7 +
    // 約定価格 / 約定量 / 残数量 (isPost 限定)
    (isPost ? 3 : 0) +
    // 入札番号 (AC-BSP-001)
    1 +
    // メモ (JEPX提出用) / メモ (自由記述) / ワークフローID / 作成
    4 +
    (hasRowActions ? 1 : 0)

  // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)。 コマが飛ぶ・
  // 1 コマが複数行の一覧なので、 境目をまたぐ行の下に 1 本だけ引く。
  // 入札履歴は受渡日をまたぐので、 受渡日が変わる所には引かない。
  const komaDividerFlags = komaListDividerFlags(
    rows,
    (row) => row.timeCode,
    (row) => String(row.targetDate?.getTime() ?? ''),
  )
  // 空状態 (emptyContent) は表の横幅でなく、 見えている幅の中央に置く。 列が多く横に
  // スクロールする表なので、 表の全幅で中央揃えにすると説明文とボタンが画面の右へ外れる
  // (design `spot.html` の `.empty-row-content` を sticky にしているのと同じ理由)。
  // 見えている幅を 100cqw で取れるよう、 その間だけスクロールの器を container にする。
  const showsEmptyContent = rows.length === 0 && emptyContent !== undefined
  return (
    <Box
      ref={wrapRef}
      overflowX="auto"
      // 0 件の時 (空状態の案内・1 行メッセージ) だけ、 見えている幅を 100cqw / 100cqi で
      // 取れるようスクロールの器を container にする。
      containerType={rows.length === 0 ? 'inline-size' : undefined}
    >
      <TableRoot
        data-row-hover={onOpenBid !== undefined ? '' : undefined}
        data-zebra
      >
        <TableHeader
          position="sticky"
          // 右端固定の操作列セル (zIndex 2) より上に置く (縦スクロールで見出しが隠れない)。
          zIndex={3}
          top={0}
          // [DS差異] ecloud の `TableColumnHeader` は素の chakra 既定のままで
          // éclair の TH 規定 (地 = Surface.tableHeader / 文字 = Text.midEmphasis /
          // 600 / padding 10px 12px) を持たないため、 8080 の `.ecl-table th` と
          // 同じ見た目を当てる (デザイナー指摘 2026-08-27「8080同様、DS Table 適用 (TH)」)。
          // style は共通部品の single source (`TABLE_HEADER_CELL_STYLE`) を使う。
          // `textAlign` は列ごとに違うので css には入れない (各 TH の prop が正)。
          css={{ '& th': TABLE_HEADER_CELL_STYLE }}
        >
          <TableRow>
            {/* design `spot.html:1180` の ステータス は post-only class を持たないため
                pre/post 両方で最左 (1 列目) に常時表示する (= 行の状態を最左に集約)。
                承認・送信・約定の 3 軸から導いた 12 区分 (AC-BSP-047) のいずれかを
                出す (区分は呼び出し側の `statusCategoryOf`、 描画は [[SpotBidStatusBadge]])。 */}
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.status}
              // 見出しも cell と同じ左寄せにする (devin-review PR #5804)。
              // cell 側は design の td に合わせて左寄せなので、 見出しだけ中央だと
              // 見出しと中身の水平位置がずれる。
              textAlign="left"
            >
              {t('page:biddingSpot.column.status')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.timeBand}>
              {t('page:biddingSpot.column.timeBand')}
            </TableColumnHeader>
            {/* 受渡契約コード (AC-BSP-001。 2026-09-08 spec 決定で発注主体
                〈区分ラベル + 事業者名〉列を置換)。 発注主体区分 × 入札エリア
                から広域マスタを自動解決したコードで、 どの発注主体で発注して
                いるかを本コードで判別する (誤った発注主体での発注に気づける
                ようにするため)。 固定値列ではなく生成コードなので、 メモ等の
                テキスト列と同じ左寄せにする。 */}
            <TableColumnHeader minWidth={COL_MIN_WIDTH.deliveryContractCode}>
              {t('page:biddingSpot.column.deliveryContractCode')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.area} textAlign="center">
              {t('page:biddingSpot.column.area')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.side} textAlign="center">
              {t('page:biddingSpot.column.side')}
            </TableColumnHeader>
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.bidPrice}
              textAlign="end"
            >
              {t('page:biddingSpot.column.bidPrice')}
            </TableColumnHeader>
            {/* コマ別の数量は MW 表記 (AC-BSP-053 / design `spot.html:1335`)。
                保持値もコマ別 MW なのでセルは換算しない (issue #4285)。 */}
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.bidVolume}
              textAlign="end"
            >
              {t('page:biddingSpot.column.bidVolumeMw')}
            </TableColumnHeader>
            {isPost && (
              <>
                <TableColumnHeader
                  minWidth={COL_MIN_WIDTH.execPrice}
                  textAlign="end"
                >
                  {t('page:biddingSpot.column.execPrice')}
                </TableColumnHeader>
                <TableColumnHeader
                  minWidth={COL_MIN_WIDTH.execVolume}
                  textAlign="end"
                >
                  {t('page:biddingSpot.column.execVolumeMw')}
                </TableColumnHeader>
                <TableColumnHeader
                  minWidth={COL_MIN_WIDTH.remainingVolume}
                  textAlign="end"
                >
                  {t('page:biddingSpot.column.remainingVolumeMw')}
                </TableColumnHeader>
              </>
            )}
            {/* 入札番号 (AC-BSP-001。 JEPX が発番する番号〈JEPX 取引規程 第37条〉)。
                design `spot.html` の `th.num` に合わせて右寄せにする。 */}
            <TableColumnHeader minWidth={COL_MIN_WIDTH.bidNo} textAlign="end">
              {t('page:biddingSpot.column.bidNo')}
            </TableColumnHeader>
            {/* 独立した「承認」列は持たない (AC-BSP-059)。 承認申請中 / 差戻しは
                ステータス列 (最左セル、 AC-BSP-047) に統合済み。 */}
            {/* メモ 2 欄 (design `spot.html:1253` 準拠。 JEPX提出用 / 自由記述)。 */}
            <TableColumnHeader minWidth={COL_MIN_WIDTH.memoJepx}>
              {t('page:biddingSpot.column.memoJepx')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.memoFree}>
              {t('page:biddingSpot.column.memoFree')}
            </TableColumnHeader>
            {/* トランシェ列は P3 で対応予定のため、それまで非表示 */}
            {/* <TableColumnHeader>{t('page:biddingSpot.column.tranche')}</TableColumnHeader> */}
            {/* ワークフローID (AC-BSP-001。 design `spot.html:1484` title「業務ワークフロー
                (ケース) からの遷移時に自動付与される来歴」)。 手動作成の行は空になるが、
                spec が常設列として要求するため出し分けはしない。 */}
            <TableColumnHeader minWidth={COL_MIN_WIDTH.workflowId}>
              {t('page:biddingSpot.column.workflowId')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.createdAt}>
              {t('page:biddingSpot.column.createdAt')}
            </TableColumnHeader>
            {/* 操作列は **フェーズで出し分けない**。 GC 後 (post) は編集・削除ができ
                ないが、 「詳細」 で開く導線がここにしか無い (#5356 Des 裁定「全行開く・
                下書き以外は表示専用」)。 以前は `!isPost` で列ごと出しておらず、 約定済の
                受渡日では行クリックも ⋯ も無く**モーダルを開く手段が皆無**だった
                (2026-08-19 デザイナー指摘「行クリックしても modal 開かない」)。
                分ける軸はフェーズではなく **その呼び出しに操作があるか** (`hasRowActions`)。
                [#5362] `width` は min-content 分配モードで無視されるため `minWidth` に
                置き換える (design も同じ理由で `min-width` を採っている)。 */}
            {hasRowActions && (
              <TableColumnHeader
                minWidth={COL_MIN_WIDTH.actions}
                textAlign="center"
                aria-label={t('common:table.actionsLabel')}
                data-sticky-shadow={stickyShadow}
                style={{
                  ...TABLE_HEADER_CELL_STYLE,
                  ...stickyActionHeaderStyle(TABLE_HEADER_BG),
                }}
              />
            )}
          </TableRow>
        </TableHeader>
        {/* TD は design `.ecl-table td` の 6px 10px (UI 辞書 table.md §0) */}
        <TableBody>
          {rows.length === 0 &&
            (showsEmptyContent ? (
              <TableRow>
                <TableCell textAlign="center" colSpan={columnCount}>
                  <Box position="sticky" left={0} width="100cqw">
                    {emptyContent}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              // 案内を持たない 0 件は表の 1 行メッセージ (UI 辞書 table.md §3:
              // 88px・Text.midEmphasis・md)。
              <TableMessageRow
                colSpan={columnCount}
                message={t('page:biddingSpot.bidTable.empty')}
              />
            ))}
          {rows.map((row, rowIndex) => {
            const highlightKind = resolveRowHighlight(
              row.id,
              inversionBidIds,
              limitViolationBidIds,
            )
            const highlight =
              highlightKind === undefined
                ? undefined
                : ROW_HIGHLIGHT[highlightKind]
            // 編集・削除できるのは DRAFT request の札だけ (AC-066 / AC-082b)。
            // 画面が read-only の時は呼び出し側がハンドラを渡さないので、
            // 「ハンドラがある」= その操作を出してよい、 と揃えて判定する。
            const canEditRow =
              row.approvalState === BiddingApprovalState.DRAFT &&
              onEditBid !== undefined
            const canDeleteRow =
              row.approvalState === BiddingApprovalState.DRAFT &&
              onDeleteBid !== undefined
            // 取下 / 再入札 の出し分け (AC-BSP-057 / AC-BSP-081)。 状態の判定は
            // model に閉じ、 ここでは「ハンドラがあるか」 だけを重ねる
            // (= 編集・削除と同じ規則)。
            const withdrawAction = resolveSpotBidWithdrawAction({
              bid: row,
              isBeforeDeadline,
              isWorkflowReadonly,
            })
            const canWithdrawRow =
              withdrawAction === 'withdraw' && onWithdrawBid !== undefined
            const canReturnRowToDraft =
              onReturnToDraft !== undefined &&
              canReturnToDraft({
                target: row,
                isBeforeDeadline,
                isWorkflowReadonly,
              })
            const canRebidRow =
              withdrawAction === 'rebid' && onRebidBid !== undefined
            const bidPrice = Number(row.priceYenPerKwh)
            const execPrice =
              row.execPriceYenPerKwh !== undefined
                ? Number(row.execPriceYenPerKwh)
                : undefined
            const execVol =
              row.execVolumeMwh !== undefined
                ? Number(row.execVolumeMwh)
                : undefined
            const category = statusCategoryOf(row)
            const withdrawState = resolveSpotBidWithdrawState(row.bidStatus)
            const remainingVol = spotBidRemainingVolumeMw(row, category)
            // 作成列 (AC-BSP-001。 design `spot.html` `.col-audit`)。
            const createdLabel = formatCreatedLabel(
              resolveMemberDisplayName(row.createdBy),
              row.createdAt,
            )
            // 「複製」(AC-BSP-011) は失効が確定した札のみ (= 送信をやり直す意味が
            // 無い終端区分に限る)。 ステータス列と同じ区分 (`statusCategoryOf`) に加え、
            // JEPX が受け付けていたかを確かめ終えていない札 (送信中のまま等) は
            // 区分が 「失効」 でも出さない (AC-BSP-117、 [[isExpiryConfirmed]])。
            const canDuplicateRow = canDuplicate(row)
            return (
              <TableRow
                key={row.id}
                // 縞と行 hover は表の recipe (system.ts の table `data-zebra` / `data-row-hover`)。 売買逆転・制限違反の
                // 行の地は縞 / hover より優先する (状態を示す地を hover で消さない)。
                _hover={
                  onOpenBid !== undefined && highlight === undefined
                    ? STICKY_ACTION_HOVER_OVERLAY
                    : undefined
                }
                cursor={onOpenBid !== undefined ? 'pointer' : undefined}
                // 行 hover の地は操作列にも重ねる (強調地の行は hover で色を変えないので重ねない)。
                backgroundColor={highlight?.backgroundColor}
                css={
                  komaDividerFlags[rowIndex] === true
                    ? KOMA_DIVIDER_ROW_CSS
                    : undefined
                }
                data-row-highlight={highlightKind}
                onClick={
                  onOpenBid !== undefined
                    ? (event) => {
                        // 行内の操作 (⋯ メニュー・ボタン・リンク) のクリックでは
                        // 開かない (design も `button, a, label.btn` を除外する)。
                        // これが無いと「削除」を押した瞬間に編集も開いてしまう。
                        if (
                          event.target instanceof Element &&
                          event.target.closest(
                            'button, a, [role="menuitem"]',
                          ) !== null
                        ) {
                          return
                        }
                        onOpenBid(row)
                      }
                    : undefined
                }
              >
                {/* ステータス (= design 最左列、 pre/post 常時表示)。 */}
                <TableCell
                  minWidth={COL_MIN_WIDTH.status}
                  // design `bidding/spot.html` のステータス cell は左寄せ。
                  textAlign="left"
                >
                  <Box
                    alignItems="center"
                    justifyContent="flex-start"
                    gap={1}
                    display="inline-flex"
                  >
                    <BidStatusAlertIcons
                      isInversion={inversionBidIds?.has(row.id) === true}
                      isLimitViolation={
                        limitViolationBidIds?.has(row.id) === true
                      }
                    />
                    {hasSpotSendErrorReason(row, category) ? (
                      <SendErrorReasonTooltip
                        errorCategory={row.sendErrorCategory}
                        errorDetail={row.sendErrorDetail}
                        gateClosedNotice={
                          isExpiryConfirmedRow(row)
                            ? {
                                message: t(
                                  'page:biddingSpot.sendErrorGateClosed.message',
                                ),
                                hint: canDuplicateRow
                                  ? t(
                                      'page:biddingSpot.sendErrorGateClosed.reuseHint',
                                    )
                                  : undefined,
                              }
                            : undefined
                        }
                      >
                        <SpotBidStatusBadge category={category} />
                      </SendErrorReasonTooltip>
                    ) : (
                      <SpotBidStatusBadge category={category} />
                    )}
                    {category === 'sending' && !isBeforeDeadline && (
                      <ReceiptCheckingNote />
                    )}
                    {/* 取下処理中は design (`bidding/spot.html` の doWithdraw) のとおり
                        操作セルに出す。 取下失敗は design に無いので、 ブロック入札と同じ
                        チップをステータスの横に出す (AC-BSP-057b)。 要確認の印がある間は
                        出さない (AC-BSP-125)。 */}
                    {/* このcellは親のgap={1}で4pxを確保するため二重に余白を付けない。 */}
                    {row.needsReview && <NeedsReviewChip marginLeft={0} />}
                    {listedWithdrawState(withdrawState, row.needsReview) ===
                      'withdrawFailed' && (
                      <WithdrawStateBadge
                        isPastDeadline={!isBeforeDeadline}
                        state="withdrawFailed"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.timeBand}>
                  {komaNumberTimeRange(row.timeCode, '-')}
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.deliveryContractCode}>
                  {row.deliveryContractCode !== undefined &&
                  row.deliveryContractCode !== ''
                    ? row.deliveryContractCode
                    : '—'}
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.area} textAlign="center">
                  {convertAreaLabel(row.areaCode) || '—'}
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.side} textAlign="center">
                  <TradeSideLabel side={row.direction} />
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.bidPrice}
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {/* 成行札は価格を持たない (AC-SPOT-006)。 内部表現の "0" を
                      ¥0.00 と出すと、 承認者には「0 円の指値」 としか見えず、
                      約定価格を指定しない札だと判別できない。 */}
                  {isMarketOrderRow(row)
                    ? t('page:biddingSpot.bidTable.marketOrderPrice')
                    : bidPrice.toFixed(2)}
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.bidVolume}
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {Number(row.volumeMwh).toFixed(1)}
                </TableCell>
                {isPost && (
                  <>
                    <TableCell
                      minWidth={COL_MIN_WIDTH.execPrice}
                      textAlign="end"
                      css={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {execPrice !== undefined ? execPrice.toFixed(2) : '—'}
                    </TableCell>
                    <TableCell
                      minWidth={COL_MIN_WIDTH.execVolume}
                      textAlign="end"
                      css={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {execVol !== undefined ? execVol.toFixed(1) : '—'}
                    </TableCell>
                    <TableCell
                      minWidth={COL_MIN_WIDTH.remainingVolume}
                      textAlign="end"
                      css={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {remainingVol !== undefined
                        ? remainingVol.toFixed(1)
                        : '—'}
                    </TableCell>
                  </>
                )}
                {/* 入札番号。 未送信 (下書き・承認待ち・差戻し・予約中) の札は
                    JEPX が発番していないので空表示にする (AC-BSP-001)。 */}
                <TableCell
                  minWidth={COL_MIN_WIDTH.bidNo}
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {row.jepxBidNo ?? ''}
                </TableCell>
                {/* メモ 2 欄。 長文は省略 + hover 全文 (design `.col-memo` 準拠)。
                    design は `.col-memo` の max-width 180px に列フロアを合わせている
                    (= min とmax が同値) ので、 実装も maxWidth と同値の minWidth を置く。 */}
                <BidMemoCell
                  width={COL_MIN_WIDTH.memoJepx}
                  value={row.memoJepx}
                />
                <BidMemoCell
                  width={COL_MIN_WIDTH.memoFree}
                  value={row.memoFree}
                />
                {/* トランシェ列は P3 で対応予定のため、それまで非表示 */}
                {/* <TableCell>
                  {row.trancheId !== undefined
                    ? (tranchesById.get(row.trancheId) ?? row.trancheId)
                    : '—'}
                </TableCell> */}
                <TableCell minWidth={COL_MIN_WIDTH.workflowId}>
                  {row.workflowId !== undefined && row.workflowId !== ''
                    ? row.workflowId
                    : ''}
                </TableCell>
                {/* 作成列は 8080 `td.col-audit` と同じ sm / Text.midEmphasis /
                    tabular-nums。 */}
                <TableCell
                  minWidth={COL_MIN_WIDTH.createdAt}
                  maxWidth="220px"
                  color="Text.midEmphasis"
                  fontSize="sm"
                  css={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  title={createdLabel}
                >
                  {createdLabel}
                </TableCell>
                {hasRowActions && (
                  <TableCell
                    minWidth={COL_MIN_WIDTH.actions}
                    textAlign="center"
                    data-sticky-action
                    data-sticky-shadow={stickyShadow}
                    style={{
                      ...stickyActionCellStyle(rowIndex),
                      // 縞は `data-zebra` (偶数番目の行) と同じ並びで数える。 強調地の行は
                      // 行と同じ色にする。
                      ...(highlight !== undefined
                        ? { ['--sticky-fill' as string]: highlight.stickyFill }
                        : {}),
                    }}
                  >
                    {/* 編集・削除は DRAFT request の札のみ表示する
                        (AC-066 / AC-082b。 BlockBidTable の canManageGroup と同型)。 */}
                    {/* ⋯ メニューは**全行に出す**。 中身は「その行で実際にできる
                        こと」で決める:
                        - 編集・削除できる (= DRAFT かつハンドラがある) → 編集 / 削除
                        - できない → 詳細 (表示専用で開く)
                        - 失効している (= canDuplicateRow) → 上記に加えて複製
                          (AC-BSP-011、 詳細と排他ではなく併存する)

                        以前は下書きの行にしか出しておらず、 下書き以外の札はモーダルを
                        開く導線が 1 つも無かった (2026-08-18 デザイナー指摘
                        「逆転している行の詳細 modal を開く導線がない」)。
                        `approvalState` だけで分岐すると、 画面が read-only (`?workflowId=`)
                        で DRAFT 行がある場合に **disabled な 編集/削除 だけ**が並び、
                        キーボードでは詳細に到達できない (PR #5873 codex-review 指摘)。
                        行クリックだけで済ませないのは、 表の行は tab で focus できず
                        キーボードだけの利用者が開けないため (WCAG 2.1.1)。 */}
                    {withdrawState === 'withdrawing' && (
                      <Text
                        as="span"
                        color="Text.midEmphasis"
                        fontSize="sm"
                        whiteSpace="nowrap"
                        title={t(withdrawStateHintKey('withdrawing', false))}
                      >
                        {t('page:biddingSpot.withdrawState.pendingInOperation')}
                      </Text>
                    )}
                    {(canEditRow ||
                      canDeleteRow ||
                      canDuplicateRow ||
                      canWithdrawRow ||
                      canReturnRowToDraft ||
                      canRebidRow ||
                      onOpenBid !== undefined) && (
                      <MenuRoot>
                        <MenuTrigger
                          aria-label={t('common:table.actionsLabel')}
                        >
                          {/* icon だけのボタンなので hover でボタン名を出す
                              (BlockBidTable の行の ⋯ と同じ。 dev 共通の DS Tooltip)。 */}
                          <Tooltip content={t('common:table.actionsLabel')}>
                            <IconButton
                              as="span"
                              aria-label={t('common:table.actionsLabel')}
                              size="sm"
                              variant="ghost"
                            >
                              {/* 行の ⋯ は DS 由来の icon を使う (design/CLAUDE.md
                              `.ecl-icon-btn--sm` = 「⋯ 等、 DS 由来の小型 icon-btn」/
                              16px)。 文字リテラルだとフォント依存で、 同じ画面の
                              ヘッダ側 ⋯ (2026-08-31 に icon 化済み) と point が
                              揃わない。 枠なし (ghost) なのは**テーブル行内のみ**の
                              例外で、 ヘッダ側は枠ありのまま。 */}
                              <FaEllipsisRegular
                                boxSize={iconSize.gridInline}
                              />
                            </IconButton>
                          </Tooltip>
                        </MenuTrigger>
                        <MenuContent>
                          {canEditRow && (
                            <MenuItem
                              onSelect={() => onEditBid?.(row)}
                              value="edit"
                            >
                              {t('common:action.edit')}
                            </MenuItem>
                          )}
                          {canDeleteRow && (
                            <MenuItem
                              color="Text.negativeHigh"
                              onSelect={() => onDeleteBid?.(row)}
                              value="delete"
                            >
                              {t('common:action.delete')}
                            </MenuItem>
                          )}
                          {canWithdrawRow && (
                            <MenuItem
                              onSelect={() => onWithdrawBid?.(row)}
                              value="withdraw"
                            >
                              {t('page:biddingSpot.action.withdraw')}
                            </MenuItem>
                          )}
                          {canReturnRowToDraft && (
                            <MenuItem
                              onSelect={() => onReturnToDraft?.(row)}
                              value="returnToDraft"
                            >
                              {t('page:biddingSpot.action.returnToDraft')}
                            </MenuItem>
                          )}
                          {canRebidRow && (
                            <ButtonTooltip content={memberBidDisabledReason}>
                              <MenuItem
                                disabled={memberBidDisabledReason !== undefined}
                                onSelect={() => onRebidBid?.(row)}
                                title={memberBidDisabledReason}
                                value="rebid"
                              >
                                {t('page:biddingSpot.action.rebid')}
                              </MenuItem>
                            </ButtonTooltip>
                          )}
                          {canDuplicateRow && (
                            <ButtonTooltip content={memberBidDisabledReason}>
                              <MenuItem
                                disabled={memberBidDisabledReason !== undefined}
                                onSelect={() => onDuplicateBid?.(row)}
                                title={memberBidDisabledReason}
                                value="duplicate"
                              >
                                {t('page:biddingSpot.action.duplicate')}
                              </MenuItem>
                            </ButtonTooltip>
                          )}
                          {/* 「詳細」 は取下 / 再入札と**併記**する。 編集・削除が
                              できない行で唯一モーダルを開ける導線であり、 表の行は
                              tab で focus できないためキーボード利用者はここから
                              しか到達できない (WCAG 2.1.1)。 */}
                          {!canEditRow &&
                            !canDeleteRow &&
                            onOpenBid !== undefined && (
                              <MenuItem
                                onSelect={() => onOpenBid(row)}
                                value="detail"
                              >
                                {t('common:action.detail')}
                              </MenuItem>
                            )}
                        </MenuContent>
                      </MenuRoot>
                    )}
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </TableRoot>
    </Box>
  )
}
